import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { ChevronDown, ChevronUp, Tag, RefreshCw, X } from "lucide-react";
import { useDatasets } from "../context/DatasetContext";
import { usePersistedState } from "../hooks/usePersistedState";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartyAuditPanel() {
  const { debouncedSelectedIds } = useDatasets();
  const [collapsed, setCollapsed] = usePersistedState("ui.partyAudit.collapsed", true);
  const [unknown, setUnknown] = useState([]);
  const [tagged, setTagged] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tagging, setTagging] = useState(null);
  const [message, setMessage] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const dsParam = debouncedSelectedIds ? `&dataset_ids=${debouncedSelectedIds}` : "";
      const [u, t] = await Promise.all([
        axios.get(`${API}/party/unknown-committees?limit=100${dsParam}`),
        axios.get(`${API}/party/tagged-committees`),
      ]);
      setUnknown(u.data.committees || []);
      setTagged(t.data.overrides || []);
    } catch (e) {
      setMessage({ type: "error", text: e.response?.data?.detail || "Failed to load" });
    } finally {
      setLoading(false);
    }
  }, [debouncedSelectedIds]);

  useEffect(() => {
    if (!collapsed) fetchAll();
  }, [collapsed, fetchAll]);

  const tag = async (name, party) => {
    setTagging(`${name}:${party}`);
    setMessage(null);
    try {
      await axios.post(`${API}/party/tag-committee`, { committee_name: name, party });
      setMessage({ type: "success", text: `Tagged "${name}" as ${party}` });
      window.dispatchEvent(new CustomEvent("party-tag-changed", { detail: { name, party, action: "tag" } }));
      await fetchAll();
    } catch (e) {
      setMessage({ type: "error", text: e.response?.data?.detail || "Failed to tag" });
    } finally {
      setTagging(null);
    }
  };

  const untag = async (name) => {
    if (!window.confirm(`Remove party tag for "${name}"?`)) return;
    setTagging(`untag:${name}`);
    try {
      await axios.delete(`${API}/party/tag-committee`, { data: { committee_name: name } });
      setMessage({ type: "success", text: `Removed tag for "${name}"` });
      window.dispatchEvent(new CustomEvent("party-tag-changed", { detail: { name, action: "untag" } }));
      await fetchAll();
    } catch (e) {
      setMessage({ type: "error", text: e.response?.data?.detail || "Failed to untag" });
    } finally {
      setTagging(null);
    }
  };

  const fmtDollar = (v) => {
    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
    if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
    return `$${v.toFixed(0)}`;
  };

  return (
    <Card data-testid="party-audit-panel">
      <CardHeader
        className="cursor-pointer select-none"
        onClick={() => setCollapsed((c) => !c)}
        data-testid="party-audit-toggle"
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Tag className="w-4 h-4" /> Party Classification Audit
          </CardTitle>
          <div className="flex items-center gap-2">
            {!collapsed && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7"
                onClick={(e) => { e.stopPropagation(); fetchAll(); }}
                data-testid="party-audit-refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              </Button>
            )}
            {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Tag unknown committees as Republican or Democrat to fix gray map pins. Tags apply globally and instantly.
          {debouncedSelectedIds
            ? " Showing committees from the currently selected datasets."
            : " Select one or more datasets on the left to narrow the audit."}
        </p>
      </CardHeader>

      {!collapsed && (
        <CardContent className="space-y-6" data-testid="party-audit-content">
          {message && (
            <div
              className={`text-sm px-3 py-2 rounded-sm ${
                message.type === "success"
                  ? "bg-green-50 text-green-700 border border-green-200"
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}
              data-testid="party-audit-message"
            >
              {message.text}
            </div>
          )}

          {/* Tagged section */}
          <div>
            <h4 className="text-sm font-semibold mb-2">
              Your Tags ({tagged.length})
            </h4>
            {tagged.length === 0 ? (
              <p className="text-xs text-muted-foreground">No custom tags yet.</p>
            ) : (
              <div className="border rounded-sm divide-y">
                {tagged.map((t) => (
                  <div
                    key={t.committee_name}
                    className="flex items-center justify-between px-3 py-2 text-sm"
                    data-testid={`tagged-row-${t.committee_name}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          t.party === "republican"
                            ? "bg-red-100 text-red-700"
                            : t.party === "democrat"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {t.party}
                      </span>
                      <span className="truncate" title={t.committee_name}>
                        {t.committee_name}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-muted-foreground hover:text-red-600"
                      disabled={tagging === `untag:${t.committee_name}`}
                      onClick={() => untag(t.committee_name)}
                      data-testid={`untag-btn-${t.committee_name}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Unknown section */}
          <div>
            <h4 className="text-sm font-semibold mb-2">
              Top Unknown Committees ({unknown.length})
            </h4>
            {loading ? (
              <p className="text-xs text-muted-foreground">Loading...</p>
            ) : unknown.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                All committees are classified. 🎉
              </p>
            ) : (
              <div className="border rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60">
                    <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="text-left px-3 py-2 font-medium">Committee</th>
                      <th className="text-right px-3 py-2 font-medium">Total</th>
                      <th className="text-right px-3 py-2 font-medium hidden sm:table-cell">Donations</th>
                      <th className="text-right px-3 py-2 font-medium">Tag as</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {unknown.map((c) => (
                      <tr
                        key={c.committee_name}
                        className="hover:bg-muted/30"
                        data-testid={`unknown-row-${c.committee_name}`}
                      >
                        <td className="px-3 py-2 truncate max-w-[300px]" title={c.committee_name}>
                          <a
                            href={`https://www.google.com/search?q=${encodeURIComponent(`"${c.committee_name}" FEC committee`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline hover:text-primary/80"
                            data-testid={`search-committee-${c.committee_name}`}
                          >
                            {c.committee_name}
                          </a>
                        </td>
                        <td className="px-3 py-2 text-right font-mono">
                          {fmtDollar(c.total_amount)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-muted-foreground hidden sm:table-cell">
                          {c.donation_count.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs border-red-300 text-red-700 hover:bg-red-50"
                              disabled={tagging === `${c.committee_name}:republican`}
                              onClick={() => tag(c.committee_name, "republican")}
                              data-testid={`tag-rep-${c.committee_name}`}
                            >
                              Rep
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                              disabled={tagging === `${c.committee_name}:democrat`}
                              onClick={() => tag(c.committee_name, "democrat")}
                              data-testid={`tag-dem-${c.committee_name}`}
                            >
                              Dem
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs border-slate-300 text-slate-600 hover:bg-slate-50"
                              disabled={tagging === `${c.committee_name}:skip`}
                              onClick={() => tag(c.committee_name, "skip")}
                              title="Hide from future audits"
                              data-testid={`tag-skip-${c.committee_name}`}
                            >
                              Skip
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}
